import {
  VStack,
  Heading,
  Stack,
  HeadingProps,
  Flex,
  StackProps,
} from '@chakra-ui/react';
import Button from 'ui/molecules/Button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSwipe from 'features/swipe/useSwipe';
import React, { ReactNode } from 'react';

type Props = {
  title?: string | ReactNode;

  buttonLabel?: string;
  buttonCallback?: () => void;
  buttonDisabled?: boolean;
  button?: ReactNode; // override default butotn

  backButton?: boolean;
  backRoute?: string;
  backCallback?: () => void;
  skipButtonLabel?: string;
  skipButtonCallback?: () => void;
  stackProps?: StackProps;

  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;

  children: React.ReactNode;
  buttonSticky?: boolean;

  footer?: React.ReactNode;
  layoutStyle?: StackProps;
  titleStyle?: HeadingProps;
};

const PageLayout = ({
  title,
  buttonLabel,
  buttonCallback,
  button,
  backButton = false,
  backRoute,
  backCallback,
  buttonDisabled = false,
  buttonSticky = false,
  stackProps,
  skipButtonLabel,
  skipButtonCallback,
  onSwipeLeft,
  onSwipeRight,
  footer,
  children,
  layoutStyle,
  titleStyle,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useSwipe({ left: onSwipeLeft, right: onSwipeRight });

  return (
    <VStack
      display="flex"
      height="100%"
      justifyContent="space-around"
      pb={{
        base: '30px',
        md: '30px',
      }}
      width={{
        base: '90vw',
        md: '600px',
      }}
      {...layoutStyle}
    >
      {title && (
        <Heading
          textAlign="center"
          zIndex={2} // this allows titles to always be on top of videos
          fontSize={{
            base: '24px',
            md: '29px',
          }}
          pt={{
            base: '20px',
            md: '60px',
          }}
          color="titleColor"
          lineHeight="100%"
          fontWeight="normal"
          pb={{
            base: '20px',
            md: '30px',
          }}
          {...titleStyle}
        >
          {title}
        </Heading>
      )}

      {children}

      {(buttonLabel || button) && (
        <Stack
          position={buttonSticky ? 'sticky' : 'relative'}
          bottom="0px"
          display="flex"
          p={{ base: '10px 50px', md: '60px 0px 0px 0px' }}
          justifyContent="center"
          direction={{
            base: 'column',
            md: 'row-reverse',
          }}
          alignItems="center"
          flexGrow={0.3}
          {...stackProps}
        >
          {skipButtonLabel && skipButtonCallback && (
            <Flex justifyContent={{ base: 'center', md: 'left' }}>
              <Button
                variant="skip"
                onClick={skipButtonCallback}
                transition="all 0.3s"
              >
                {skipButtonLabel}
              </Button>
            </Flex>
          )}

          <Flex justifyContent={{ base: 'center', md: 'center' }}>
            {button || (
              <Button onClick={buttonCallback} disabled={buttonDisabled}>
                {buttonLabel}
              </Button>
            )}
          </Flex>

          {backButton && (
            <Flex
              justifyContent={{ base: 'center', md: 'right' }}
              // in case of skip button, we need to add some padding to the button so it's visually centered
              minW={skipButtonLabel ? '200px' : '0px'}
            >
              <Button
                variant="backButton"
                onClick={
                  backCallback ||
                  (backRoute ? () => navigate(backRoute) : () => navigate(-1))
                }
              >
                {t('common.backButton')}
              </Button>
            </Flex>
          )}

          {/* </HStack> */}
        </Stack>
      )}

      {footer}
    </VStack>
  );
};

export default PageLayout;
